import { FaCalculator, FaPlus, FaSearch, FaSpinner, FaTrash } from 'react-icons/fa';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

const Api5Form = ({ state, updateState }) => {
    const formData = state.formData || {};
    const activityEntries = state.activityEntries || [];
    const spendEntries = state.spendEntries || [];
    const unitsEntries = state.unitsEntries || [];
    const CLIMATIQ_API_KEY = process.env.REACT_APP_CLIMATIQ_API_KEY || 'Y2ZBNWZKRzRGNzJYNjRQMzpa';

    // Define modes for each input method
    const activityModes = ['Road', 'Rail', 'Air'];
    const spendModes = ['Rail', 'Ocean', 'Air', 'Courier/Parcel']; // Removed Road
    const unitsModes = ['Road', 'Rail', 'Air']; // Removed Ocean and Courier/Parcel

    const roadTruckClasses = ['Light-duty', 'Medium-duty', 'Heavy-duty'];
    const oceanTypes = ['Container (20ft)', 'Container (40ft)', 'Bulk'];
    const airServices = ['Express', 'Economy'];
    const railTypes = ['Diesel', 'Electric'];
    const coverageOptions = ['Line-haul only', 'Line-haul + last mile', 'All-in'];

    // Helper function to get modes based on input method
    const getModesForInputMethod = (inputMethod) => {
        switch (inputMethod) {
            case 'activity':
                return activityModes;
            case 'spend':
                return spendModes;
            case 'units':
                return unitsModes;
            default:
                return spendModes;
        }
    };

    const transportSearchConfig = {
        'Road': {
            queries: ['freight_vehicle-vehicle_type_truck', 'smartway_co2_rank', 'road freight'],
            preferred_unit_types: ['Mass/Distance'],
            fallback_activity_id: 'freight_vehicle-vehicle_type_truck-freight_category_general-smartway_co2_rank_5'
        },
        'Rail': {
            queries: ['rail freight', 'train freight'],
            preferred_unit_types: ['Mass/Distance'],
            fallback_activity_id: 'freight_train-route_type_na-fuel_source_na'
        },
        'Ocean': {
            queries: ['ocean freight', 'container ship', 'maritime freight'],
            preferred_unit_types: ['Mass/Distance'],
            fallback_activity_id: 'passenger_ferry-route_type_na-fuel_source_na'
        },
        'Air': {
            queries: ['air freight', 'air cargo'],
            preferred_unit_types: ['Mass/Distance'],
            fallback_activity_id: 'passenger_flight-route_type_na-class_na-occupancy_na'
        },
        'Courier/Parcel': {
            queries: ['light commercial vehicle', 'delivery vehicle'],
            preferred_unit_types: ['Mass/Distance'],
            fallback_activity_id: 'passenger_vehicle-vehicle_type_van-fuel_type_na-engine_size_na'
        }
    };

    const defaultEmissionFactors = {
        'Road': { activity_id: "freight_vehicle-vehicle_type_general-fuel_source_na-vehicle_weight_na-distance_uplift_included", source: "EPA", region: "US", year: 2023, unit: "t*km", unit_type: "Mass/Distance" },
        'Rail': { activity_id: "freight_train-route_type_rail_transportation-fuel_type_na-load_type_na-train_type_na", source: "DEFRA", region: "GLOBAL", year: 2023, unit: "t*km", unit_type: "Mass/Distance" },
        'Ocean': { activity_id: "sea_freight-vessel_type_water_transportation-route_type_na-vessel_length_na-tonnage_na-fuel_source_na-load_type_na-distance_uplift_na", source: "DEFRA", region: "GLOBAL", year: 2023, unit: "t*km", unit_type: "Mass/Distance" },
        'Air': { activity_id: "freight_flight-route_type_air_transportation-distance_na-weight_na-rf_na-method_na-aircraft_type_na-distance_uplift_na", source: "DEFRA", region: "GLOBAL", year: 2023, unit: "t*km", unit_type: "Mass/Distance" },
        'Courier/Parcel': { activity_id: "communication_services-type_postal_and_courier_services", source: "BEIS", region: "GB", year: 2019, unit: "t*km", unit_type: "Mass/Distance" }
    };

    // Add this function to handle input method change
    const handleInputMethodChange = (method) => {
        updateState({
            formData: {
                ...formData,
                inputMethod: method
            }
        });
    };

    // Search for quantity-based emission factors
    const searchQuantityEmissionFactors = async (mode) => {
        try {
            const config = transportSearchConfig[mode];
            if (!config) return defaultEmissionFactors[mode];

            let bestFactor = null;
            for (const query of config.queries) {
                const params = new URLSearchParams({
                    query: query,
                    data_version: '^0',
                    region: 'US',
                    year: formData.year || new Date().getFullYear().toString(),
                    results_per_page: '10'
                });

                const response = await fetch(`https://api.climatiq.io/data/v1/search?${params.toString()}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) continue;
                const data = await response.json();

                if (data.results && data.results.length > 0) {
                    for (const unitType of config.preferred_unit_types) {
                        const compatibleFactors = data.results.filter(factor => factor.unit_type === unitType);
                        if (compatibleFactors.length > 0) {
                            bestFactor = compatibleFactors[0];
                            break;
                        }
                    }
                    if (bestFactor) break;
                    bestFactor = data.results[0];
                    break;
                }
            }

            if (!bestFactor) bestFactor = defaultEmissionFactors[mode];
            return bestFactor;
        } catch (error) {
            console.error(`Error searching emission factors for ${mode}:`, error);
            return defaultEmissionFactors[mode];
        }
    };

    // Search for spend-based emission factors
    const searchSpendEmissionFactors = async (mode) => {
        try {
            const config = transportSearchConfig[mode];
            const spendQueries = config.queries.map(query => `${query} spend money usd`);

            let bestFactor = null;
            for (const query of spendQueries) {
                const params = new URLSearchParams({
                    query: query,
                    data_version: '^0',
                    region: 'US',
                    year: formData.year || new Date().getFullYear().toString(),
                    results_per_page: '10'
                });

                const response = await fetch(`https://api.climatiq.io/data/v1/search?${params.toString()}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) continue;
                const data = await response.json();

                if (data.results && data.results.length > 0) {
                    const spendFactors = data.results.filter(factor =>
                        factor.unit_type === 'money' ||
                        factor.unit.includes('usd') ||
                        factor.unit.includes('USD')
                    );

                    if (spendFactors.length > 0) {
                        bestFactor = spendFactors[0];
                        break;
                    }

                    bestFactor = data.results[0];
                    break;
                }
            }

            // If no spend factors found, fall back to default spend activity IDs
            if (!bestFactor) {
                bestFactor = {
                    ...defaultEmissionFactors[mode],
                    activity_id: getSpendActivityId(mode)
                };
            }

            return bestFactor;
        } catch (error) {
            console.error(`Error searching spend factors for ${mode}:`, error);
            return {
                ...defaultEmissionFactors[mode],
                activity_id: getSpendActivityId(mode)
            };
        }
    };

    // Get the correct activity ID for spend-based calculations
    const getSpendActivityId = (mode) => {
        const spendActivityIds = {
            'Rail': 'freight_train-route_type_rail_transportation-fuel_type_na-load_type_na-train_type_na',
            'Ocean': 'sea_freight-vessel_type_water_transportation-route_type_na-vessel_length_na-tonnage_na-fuel_source_na-load_type_na-distance_uplift_na',
            'Air': 'freight_flight-route_type_air_transportation-distance_na-weight_na-rf_na-method_na-aircraft_type_na-distance_uplift_na',
            'Courier/Parcel': 'communication_services-type_postal_and_courier_services'
        };
        return spendActivityIds[mode] || defaultEmissionFactors[mode].activity_id;
    };

    const handleSearchFactors = async () => {
        if (formData.shipsFreight !== 'yes') {
            toastr.error('Please select "Yes" for shipping freight first');
            return;
        }
        if (!formData.inputMethod) {
            toastr.error('Please select an input method');
            return;
        }

        let modesToSearch = [];
        if (formData.inputMethod === 'activity') {
            modesToSearch = [...new Set(activityEntries.map(entry => entry.mode).filter(Boolean))];
        } else if (formData.inputMethod === 'spend') {
            modesToSearch = [...new Set(spendEntries.map(entry => entry.mode).filter(Boolean))];
        } else if (formData.inputMethod === 'units') {
            modesToSearch = [...new Set(unitsEntries.map(entry => entry.mode).filter(Boolean))];
        }

        if (modesToSearch.length === 0) {
            toastr.error('Please add entries with transport modes first');
            return;
        }

        updateState({ isSearching: true, availableFactors: {}, spendFactors: {} });
        toastr.info('Searching for best emission factors...');

        try {
            let foundCount = 0;
            const newAvailableFactors = { ...state.availableFactors };
            const newSpendFactors = { ...state.spendFactors };

            // Search for all modes simultaneously
            const searchPromises = modesToSearch.map(async (mode) => {
                const [quantityFactor, spendFactor] = await Promise.all([
                    searchQuantityEmissionFactors(mode),
                    searchSpendEmissionFactors(mode)
                ]);

                if (quantityFactor) {
                    newAvailableFactors[mode] = quantityFactor;
                    foundCount++;
                }
                if (spendFactor) {
                    newSpendFactors[mode] = spendFactor;
                    foundCount++;
                }
            });

            await Promise.all(searchPromises);

            // Update state with all collected factors
            updateState({
                availableFactors: newAvailableFactors,
                spendFactors: newSpendFactors
            });

            if (foundCount > 0) {
                toastr.success(`Found emission factors for ${foundCount} transport mode(s)! Click Calculate to proceed.`);
            } else {
                toastr.warning('Using default emission factors. Click Calculate to proceed.');
            }
        } catch (error) {
            toastr.error('Search failed. Using default factors. Click Calculate to proceed.');
        } finally {
            updateState({ isSearching: false });
        }
    };

    // Quantity-based API call for activity and units entries
    const callQuantityClimateIQAPI = async (entry, entryType) => {
        try {
            const emissionFactor = state.availableFactors?.[entry.mode] || defaultEmissionFactors[entry.mode];
            if (!emissionFactor) throw new Error(`No emission factor found for ${entry.mode}`);

            let weight = 0;
            let distance = 0;

            if (entryType === 'activity') {
                weight = parseFloat(entry.weight) || 0;
                distance = parseFloat(entry.distance) || 0;
            } else if (entryType === 'units') {
                const unitCount = parseFloat(entry.unitCount) || 0;
                let loadPerUnit = parseFloat(entry.loadPerUnit) || 0;
                distance = parseFloat(entry.distance) || 0;

                if (entry.loadUnit === 'kg') {
                    loadPerUnit = loadPerUnit / 1000; // Convert kg to tonnes
                } else if (entry.loadUnit === 'percent') {
                    // Assume 80% capacity of a standard truck (25 tonnes)
                    loadPerUnit = 0.8 * 25;
                }
                weight = unitCount * loadPerUnit;
            }

            let requestBody = {};

            // Use specific activity IDs based on mode
            const activityId = getQuantityActivityId(entry.mode, emissionFactor);

            requestBody = {
                emission_factor: {
                    activity_id: activityId,
                    data_version: "^0"
                },
                parameters: {
                    weight: weight,
                    weight_unit: "t",
                    distance: distance,
                    distance_unit: "km"
                }
            };

            const response = await fetch('https://api.climatiq.io/data/v1/estimate', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`API request failed with status ${response.status}: ${errorData}`);
            }

            return await response.json();
        } catch (error) {
            console.error(`Error calling ClimateIQ API for ${entryType} entry:`, error);
            throw error;
        }
    };

    // Spend-based API call for spend entries
    const callSpendClimateIQAPI = async (entry) => {
        try {
            const emissionFactor = state.spendFactors?.[entry.mode] || {
                ...defaultEmissionFactors[entry.mode],
                activity_id: getSpendActivityId(entry.mode)
            };

            const amount = parseFloat(entry.amount) || 0;

            const requestBody = {
                emission_factor: {
                    activity_id: emissionFactor.activity_id,
                    data_version: "^0"
                },
                parameters: {
                    money: amount,
                    money_unit: "usd"
                }
            };

            // Add specific fields for Courier/Parcel
            if (entry.mode === 'Courier/Parcel') {
                requestBody.emission_factor.source = "BEIS";
                requestBody.emission_factor.region = "GB";
                requestBody.emission_factor.year = 2019;
                requestBody.emission_factor.source_lca_activity = "unknown";
            }

            const response = await fetch('https://api.climatiq.io/data/v1/estimate', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`API request failed with status ${response.status}: ${errorData}`);
            }

            return await response.json();
        } catch (error) {
            console.error(`Error calling ClimateIQ API for spend entry:`, error);
            throw error;
        }
    };

    // Get the correct activity ID for quantity-based calculations
    const getQuantityActivityId = (mode, emissionFactor) => {
        const quantityActivityIds = {
            'Road': 'freight_vehicle-vehicle_type_general-fuel_source_na-vehicle_weight_na-distance_uplift_included',
            'Rail': emissionFactor.activity_id,
            'Ocean': emissionFactor.activity_id,
            'Air': emissionFactor.activity_id,
            'Courier/Parcel': emissionFactor.activity_id
        };
        return quantityActivityIds[mode] || emissionFactor.activity_id;
    };

    const handleCalculate = async () => {
        if (formData.shipsFreight !== 'yes') {
            toastr.error('Please select "Yes" for shipping freight first');
            return;
        }
        if (!formData.inputMethod) {
            toastr.error('Please select an input method');
            return;
        }

        let entries = [];
        let entryType = '';

        if (formData.inputMethod === 'activity') {
            if (activityEntries.length === 0) {
                toastr.error('Please add at least one activity-based entry');
                return;
            }
            entries = activityEntries;
            entryType = 'activity';
        } else if (formData.inputMethod === 'spend') {
            if (spendEntries.length === 0) {
                toastr.error('Please add at least one spend-based entry');
                return;
            }
            entries = spendEntries;
            entryType = 'spend';
        } else if (formData.inputMethod === 'units') {
            if (unitsEntries.length === 0) {
                toastr.error('Please add at least one units-based entry');
                return;
            }
            entries = unitsEntries;
            entryType = 'units';
        }

        updateState({ isCalculating: true, calculationResults: {} });
        toastr.info('Calculating emissions via API...');

        try {
            const results = {};
            let hasSuccessfulCalculation = false;

            for (let i = 0; i < entries.length; i++) {
                const entry = entries[i];
                if (!entry.mode) {
                    toastr.warning(`Skipping entry ${i + 1}: No transport mode selected`);
                    continue;
                }

                try {
                    let apiResult;

                    if (entryType === 'spend') {
                        // Use spend-based API for spend entries
                        apiResult = await callSpendClimateIQAPI(entry);
                    } else {
                        // Use quantity-based API for activity and units entries
                        apiResult = await callQuantityClimateIQAPI(entry, entryType);
                    }

                    const resultKey = `${entryType}_${i}`;

                    results[resultKey] = {
                        mode: entry.mode,
                        origin: entry.origin,
                        destination: entry.destination,
                        co2e: apiResult.co2e,
                        co2e_unit: apiResult.co2e_unit,
                        calculation_method: apiResult.co2e_calculation_method,
                        activity_data: apiResult.activity_data,
                        emission_factor: entryType === 'spend' ? state.spendFactors?.[entry.mode] : state.availableFactors?.[entry.mode],
                        inputMethod: entryType
                    };

                    const methodText = entryType === 'activity' ? 'Activity-based' :
                        entryType === 'spend' ? 'Spend-based' : 'Units-based';

                    toastr.success(`Calculated emissions for ${entry.mode} (${methodText}): ${apiResult.co2e.toFixed(2)} ${apiResult.co2e_unit}`);
                    hasSuccessfulCalculation = true;
                } catch (error) {
                    console.error(`API error for entry ${i + 1}:`, error);
                    toastr.error(`Failed to calculate emissions for entry ${i + 1}: ${error.message}`);
                }
            }

            updateState({ calculationResults: results });

            if (hasSuccessfulCalculation) {
                const totalEmissions = Object.values(results).reduce((sum, result) => sum + (result.co2e || 0), 0);
                toastr.success(`Calculation complete! Total emissions: ${totalEmissions.toFixed(2)} kg CO2e`);
            } else {
                toastr.warning('No emissions could be calculated. Please check your inputs and API key.');
            }
        } catch (error) {
            toastr.error('Calculation failed. Please check your inputs and try again.');
            console.error('Calculation error:', error);
        } finally {
            updateState({ isCalculating: false });
        }
    };

    // Helper function to render mode dropdown with filtered modes
    const renderModeDropdown = (entry, index, inputMethod, onChangeHandler) => {
        const allowedModes = getModesForInputMethod(inputMethod);

        return (
            <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                value={entry.mode}
                onChange={(e) => onChangeHandler(index, 'mode', e.target.value)}
            >
                <option value="">Select mode</option>
                {allowedModes.map(mode => (
                    <option key={mode} value={mode}>{mode}</option>
                ))}
            </select>
        );
    };

    const handleAddActivityEntry = () => {
        const newEntries = [...activityEntries, {
            mode: '', origin: '', destination: '', weight: '', distance: '', shipments: '',
            serviceDetails: '', truckType: '', reefer: '', containerSize: '', airService: ''
        }];
        updateState({ activityEntries: newEntries });
    };

    const handleAddSpendEntry = () => {
        const newEntries = [...spendEntries, {
            reportingPeriod: '', mode: '', origin: '', destination: '', mixedMultiple: false,
            serviceLevel: '', truckType: '', oceanType: '', airService: '', railType: '', reefer: '',
            amount: '', currency: 'USD', vatExcluded: false, surchargesIncluded: '', coverage: '', shipments: ''
        }];
        updateState({ spendEntries: newEntries });
    };

    const handleAddUnitsEntry = () => {
        const newEntries = [...unitsEntries, {
            reportingPeriod: '', mode: '', origin: '', destination: '', unitType: '',
            unitCount: '', loadPerUnit: '', loadUnit: 'tonnes', distance: ''
        }];
        updateState({ unitsEntries: newEntries });
    };

    const handleActivityEntryChange = (index, field, value) => {
        const updated = [...activityEntries];
        updated[index][field] = value;
        updateState({ activityEntries: updated });
    };

    const handleSpendEntryChange = (index, field, value) => {
        const updated = [...spendEntries];
        updated[index][field] = value;
        updateState({ spendEntries: updated });
    };

    const handleUnitsEntryChange = (index, field, value) => {
        const updated = [...unitsEntries];
        updated[index][field] = value;
        updateState({ unitsEntries: updated });
    };

    const handleRemoveActivityEntry = (index) => {
        updateState({ activityEntries: activityEntries.filter((_, i) => i !== index) });
    };

    const handleRemoveSpendEntry = (index) => {
        updateState({ spendEntries: spendEntries.filter((_, i) => i !== index) });
    };

    const handleRemoveUnitsEntry = (index) => {
        updateState({ unitsEntries: unitsEntries.filter((_, i) => i !== index) });
    };

    const loadSampleData = () => {
        updateState({
            formData: {
                ...formData,
                shipsFreight: 'yes',
                inputMethod: 'activity',
                year: '2025'
            },
            activityEntries: [{
                mode: 'Road',
                origin: 'Los Angeles, US',
                destination: 'San Francisco, US',
                weight: '25',
                distance: '615',
                shipments: '12',
                truckType: 'Heavy-duty',
                reefer: 'no'
            }]
        });
        toastr.info('Sample data loaded. Click "Search Factors" to find emission factors.');
    };

    return (
        <div className="max-w-5xl mx-auto px-4 py-8 pb-24">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-8">
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                            <strong>API Status:</strong> {CLIMATIQ_API_KEY && CLIMATIQ_API_KEY !== 'Y2ZBNWZKRzRGNzJYNjRQMzpa' ?
                                '✅ API Key Configured' : '❌ Please set REACT_APP_CLIMATIQ_API_KEY in .env file'}
                        </p>
                    </div>

                    <div className="flex justify-end mb-6">
                        <button
                            onClick={loadSampleData}
                            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all duration-200 text-sm"
                        >
                            Load Sample Data
                        </button>
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Year *</label>
                        <select
                            value={formData.year || new Date().getFullYear().toString()}
                            onChange={(e) => updateState({ formData: { ...formData, year: e.target.value } })}
                            className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            {Array.from({ length: new Date().getFullYear() - 2009 }, (_, i) =>
                                (new Date().getFullYear() - i).toString()
                            ).map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>

                    <div className="mb-8">
                        <label className="block text-lg font-semibold text-gray-700 mb-4">
                            Q1: Does the company ship freight?
                        </label>
                        <div className="flex space-x-4 mb-6">
                            <button
                                onClick={() => updateState({ formData: { ...formData, shipsFreight: 'yes' } })}
                                className={`px-6 py-3 rounded-lg border-2 transition-all ${formData.shipsFreight === 'yes'
                                        ? 'border-green-500 bg-green-500 text-white hover:bg-green-600'
                                        : 'border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                Yes
                            </button>
                            <button
                                onClick={() => updateState({ formData: { ...formData, shipsFreight: 'no' } })}
                                className={`px-6 py-3 rounded-lg border-2 transition-all ${formData.shipsFreight === 'no'
                                        ? 'border-red-500 bg-red-500 text-white hover:bg-red-600'
                                        : 'border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                No
                            </button>
                        </div>

                        {formData.shipsFreight === 'yes' && (
                            <div className="space-y-6">
                                <div className="mb-6">
                                    <button
                                        onClick={handleSearchFactors}
                                        disabled={state.isSearching}
                                        className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {state.isSearching ? (
                                            <FaSpinner className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <FaSearch className="w-4 h-4" />
                                        )}
                                        <span>{state.isSearching ? 'Searching...' : 'Search Emission Factors'}</span>
                                    </button>
                                </div>

                                {(Object.keys(state.availableFactors || {}).length > 0 || Object.keys(state.spendFactors || {}).length > 0) && (
                                    <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                                        <h3 className="font-semibold text-green-800 mb-2">Available Emission Factors</h3>
                                        <div className="space-y-2">
                                            {Object.entries(state.availableFactors || {}).map(([mode, factor]) => (
                                                <div key={mode} className="text-sm text-green-700">
                                                    <strong>{mode} (Activity/Units):</strong> {factor.name} ({factor.source}, {factor.region})
                                                    <br />
                                                    <span className="text-xs">Unit: {factor.unit} | Type: {factor.unit_type}</span>
                                                </div>
                                            ))}
                                            {Object.entries(state.spendFactors || {}).map(([mode, factor]) => (
                                                <div key={mode} className="text-sm text-green-700">
                                                    <strong>{mode} (Spend):</strong> {factor.name} ({factor.source}, {factor.region})
                                                    <br />
                                                    <span className="text-xs">Unit: {factor.unit} | Type: {factor.unit_type}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-3">Select Input Method:</label>
                                    <div className="grid grid-cols-3 gap-4">
                                        <button
                                            onClick={() => handleInputMethodChange('activity')}
                                            className={`p-4 rounded-lg border-2 transition-all ${formData.inputMethod === 'activity'
                                                    ? 'border-green-500 bg-green-500 text-white hover:bg-green-600'
                                                    : 'border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                }`}
                                        >
                                            <div className="font-semibold mb-1">Sub-Q2: Best Practice</div>
                                            <div className="text-xs">Activity-based (Weight & Distance)</div>
                                            <div className="text-xs mt-1">Modes: Road, Rail, Air</div>
                                        </button>
                                        <button
                                            onClick={() => handleInputMethodChange('spend')}
                                            className={`p-4 rounded-lg border-2 transition-all ${formData.inputMethod === 'spend'
                                                    ? 'border-blue-500 bg-blue-500 text-white hover:bg-blue-600'
                                                    : 'border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                }`}
                                        >
                                            <div className="font-semibold mb-1">Sub-Q3: Alternative 1</div>
                                            <div className="text-xs">Spend-based</div>
                                            <div className="text-xs mt-1">Modes: Rail, Ocean, Air, Courier/Parcel</div>
                                        </button>
                                        <button
                                            onClick={() => handleInputMethodChange('units')}
                                            className={`p-4 rounded-lg border-2 transition-all ${formData.inputMethod === 'units'
                                                    ? 'border-purple-500 bg-purple-500 text-white hover:bg-purple-600'
                                                    : 'border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                }`}
                                        >
                                            <div className="font-semibold mb-1">Sub-Q4: Alternative 2</div>
                                            <div className="text-xs">Units-based</div>
                                            <div className="text-xs mt-1">Modes: Road, Rail, Air</div>
                                        </button>
                                    </div>
                                </div>

                                {/* Debug: Show current input method */}
                                {formData.inputMethod && (
                                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                        <p className="text-sm text-blue-800">
                                            <strong>Current Input Method:</strong> {formData.inputMethod}
                                            <br />
                                            <strong>Available Modes:</strong> {getModesForInputMethod(formData.inputMethod).join(', ')}
                                        </p>
                                    </div>
                                )}

                                {/* Sub-Q2: Activity-based */}
                                {formData.inputMethod === 'activity' && (
                                    <div className="space-y-4">
                                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                            <h3 className="font-semibold text-gray-700 mb-2">Sub-Q2: Best Practice - Activity-based</h3>
                                            <p className="text-sm text-gray-600">
                                                Tell us how much freight you moved (weight) and how far it went (distance), per transport method and route.
                                                <br />
                                                <strong>Available modes:</strong> Road, Rail, Air
                                            </p>
                                        </div>
                                        <button
                                            onClick={handleAddActivityEntry}
                                            className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all duration-200"
                                        >
                                            <FaPlus className="w-4 h-4" />
                                            <span>Add Freight Entry</span>
                                        </button>

                                        {activityEntries.map((entry, index) => {
                                            const resultKey = `activity_${index}`;
                                            const result = state.calculationResults?.[resultKey];

                                            return (
                                                <div key={index} className="p-6 border-2 border-gray-200 rounded-lg space-y-4">
                                                    <div className="flex justify-between items-center">
                                                        <h4 className="font-semibold">Entry {index + 1}</h4>
                                                        <button
                                                            onClick={() => handleRemoveActivityEntry(index)}
                                                            className="text-red-500 hover:text-red-700 transition-colors duration-200"
                                                        >
                                                            <FaTrash className="w-4 h-4" />
                                                        </button>
                                                    </div>

                                                    {result && (
                                                        <div className="p-3 bg-green-100 rounded-lg border border-green-200">
                                                            <p className="text-sm text-green-800 font-medium">
                                                                Emissions: {result.co2e.toFixed(2)} {result.co2e_unit}
                                                            </p>
                                                            <p className="text-xs text-green-600">
                                                                {entry.mode}: {entry.origin} → {entry.destination}
                                                            </p>
                                                        </div>
                                                    )}

                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Mode</label>
                                                            {renderModeDropdown(entry, index, 'activity', handleActivityEntryChange)}
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">1. Lane: From (city/port, country)</label>
                                                            <input
                                                                type="text"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                placeholder="e.g., Los Angeles, US"
                                                                value={entry.origin}
                                                                onChange={(e) => handleActivityEntryChange(index, 'origin', e.target.value)}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">To (city/port, country)</label>
                                                            <input
                                                                type="text"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                placeholder="e.g., San Francisco, US"
                                                                value={entry.destination}
                                                                onChange={(e) => handleActivityEntryChange(index, 'destination', e.target.value)}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">2. Total weight moved: tonnes (t)</label>
                                                            <input
                                                                type="number"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                value={entry.weight}
                                                                onChange={(e) => handleActivityEntryChange(index, 'weight', e.target.value)}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">3. Average distance per shipment or total lane distance: km</label>
                                                            <input
                                                                type="number"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                value={entry.distance}
                                                                onChange={(e) => handleActivityEntryChange(index, 'distance', e.target.value)}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">4. Number of shipments (if known)</label>
                                                            <input
                                                                type="number"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                value={entry.shipments}
                                                                onChange={(e) => handleActivityEntryChange(index, 'shipments', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="col-span-2">
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">5. Service details (optional)</label>
                                                            {entry.mode === 'Road' && (
                                                                <div className="space-y-2">
                                                                    <select
                                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                        value={entry.truckType}
                                                                        onChange={(e) => handleActivityEntryChange(index, 'truckType', e.target.value)}
                                                                    >
                                                                        <option value="">Select truck type</option>
                                                                        {roadTruckClasses.map(type => <option key={type} value={type}>{type}</option>)}
                                                                    </select>
                                                                    <select
                                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                        value={entry.reefer}
                                                                        onChange={(e) => handleActivityEntryChange(index, 'reefer', e.target.value)}
                                                                    >
                                                                        <option value="">Refrigerated?</option>
                                                                        <option value="yes">Yes</option>
                                                                        <option value="no">No</option>
                                                                    </select>
                                                                </div>
                                                            )}
                                                            {entry.mode === 'Air' && (
                                                                <select
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                    value={entry.airService}
                                                                    onChange={(e) => handleActivityEntryChange(index, 'airService', e.target.value)}
                                                                >
                                                                    <option value="">Select air service</option>
                                                                    {airServices.map(service => <option key={service} value={service}>{service}</option>)}
                                                                </select>
                                                            )}
                                                            {entry.mode === 'Rail' && (
                                                                <select
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                    value={entry.serviceDetails}
                                                                    onChange={(e) => handleActivityEntryChange(index, 'serviceDetails', e.target.value)}
                                                                >
                                                                    <option value="">Select rail type</option>
                                                                    {railTypes.map(type => <option key={type} value={type}>{type}</option>)}
                                                                </select>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Sub-Q3: Spend-based */}
                                {formData.inputMethod === 'spend' && (
                                    <div className="space-y-4">
                                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                            <h3 className="font-semibold text-gray-700 mb-2">Sub-Q3: Alternative 1 - Spend-based</h3>
                                            <p className="text-sm text-gray-600">
                                                If you don't track shipment weights or distances, we can estimate using what you paid for freight.
                                                <br />
                                                <strong>Available modes:</strong> Rail, Ocean, Air, Courier/Parcel
                                            </p>
                                        </div>
                                        <button
                                            onClick={handleAddSpendEntry}
                                            className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200"
                                        >
                                            <FaPlus className="w-4 h-4" />
                                            <span>Add Spend Entry</span>
                                        </button>

                                        {spendEntries.map((entry, index) => {
                                            const resultKey = `spend_${index}`;
                                            const result = state.calculationResults?.[resultKey];

                                            return (
                                                <div key={index} className="p-6 border-2 border-gray-200 rounded-lg space-y-4">
                                                    <div className="flex justify-between items-center">
                                                        <h4 className="font-semibold">Entry {index + 1}</h4>
                                                        <button
                                                            onClick={() => handleRemoveSpendEntry(index)}
                                                            className="text-red-500 hover:text-red-700 transition-colors duration-200"
                                                        >
                                                            <FaTrash className="w-4 h-4" />
                                                        </button>
                                                    </div>

                                                    {result && (
                                                        <div className="p-3 bg-green-100 rounded-lg border border-green-200">
                                                            <p className="text-sm text-green-800 font-medium">
                                                                Emissions: {result.co2e.toFixed(2)} {result.co2e_unit}
                                                            </p>
                                                            <p className="text-xs text-green-600">
                                                                {entry.mode}: {entry.origin} → {entry.destination}
                                                            </p>
                                                        </div>
                                                    )}

                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Mode</label>
                                                            {renderModeDropdown(entry, index, 'spend', handleSpendEntryChange)}
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">1. Reporting period</label>
                                                            <input
                                                                type="text"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                placeholder="e.g., 2024 (Jan–Dec)"
                                                                value={entry.reportingPeriod}
                                                                onChange={(e) => handleSpendEntryChange(index, 'reportingPeriod', e.target.value)}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">3. Lane: From (city/port, country)</label>
                                                            <input
                                                                type="text"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                placeholder="e.g., Los Angeles, US"
                                                                value={entry.origin}
                                                                onChange={(e) => handleSpendEntryChange(index, 'origin', e.target.value)}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">To (city/port, country)</label>
                                                            <input
                                                                type="text"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                placeholder="e.g., San Francisco, US"
                                                                value={entry.destination}
                                                                onChange={(e) => handleSpendEntryChange(index, 'destination', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="col-span-2">
                                                            <label className="flex items-center space-x-2">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={entry.mixedMultiple || false}
                                                                    onChange={(e) => handleSpendEntryChange(index, 'mixedMultiple', e.target.checked)}
                                                                    className="w-4 h-4"
                                                                />
                                                                <span className="text-sm text-gray-700">If unknown or multi-stop: tick "Mixed/Multiple"</span>
                                                            </label>
                                                        </div>
                                                        <div className="col-span-2">
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">4. Service level / equipment (optional)</label>
                                                            {entry.mode === 'Ocean' && (
                                                                <select
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                    value={entry.oceanType}
                                                                    onChange={(e) => handleSpendEntryChange(index, 'oceanType', e.target.value)}
                                                                >
                                                                    <option value="">Select type</option>
                                                                    {oceanTypes.map(type => <option key={type} value={type}>{type}</option>)}
                                                                </select>
                                                            )}
                                                            {entry.mode === 'Air' && (
                                                                <select
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                    value={entry.airService}
                                                                    onChange={(e) => handleSpendEntryChange(index, 'airService', e.target.value)}
                                                                >
                                                                    <option value="">Select air service</option>
                                                                    {airServices.map(service => <option key={service} value={service}>{service}</option>)}
                                                                </select>
                                                            )}
                                                            {entry.mode === 'Rail' && (
                                                                <select
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                    value={entry.railType}
                                                                    onChange={(e) => handleSpendEntryChange(index, 'railType', e.target.value)}
                                                                >
                                                                    <option value="">Select rail type</option>
                                                                    {railTypes.map(type => <option key={type} value={type}>{type}</option>)}
                                                                </select>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">5a. Amount spent</label>
                                                            <input
                                                                type="number"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                value={entry.amount}
                                                                onChange={(e) => handleSpendEntryChange(index, 'amount', e.target.value)}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">5b. Currency</label>
                                                            <select
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                value={entry.currency}
                                                                onChange={(e) => handleSpendEntryChange(index, 'currency', e.target.value)}
                                                            >
                                                                <option value="USD">USD</option>
                                                                <option value="EUR">EUR</option>
                                                                <option value="CLP">CLP</option>
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">5c. VAT/sales tax excluded?</label>
                                                            <select
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                value={entry.vatExcluded ? 'yes' : 'no'}
                                                                onChange={(e) => handleSpendEntryChange(index, 'vatExcluded', e.target.value === 'yes')}
                                                            >
                                                                <option value="yes">Yes</option>
                                                                <option value="no">No</option>
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">5d. Surcharges included? (fuel, bunker, security)</label>
                                                            <select
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                value={entry.surchargesIncluded}
                                                                onChange={(e) => handleSpendEntryChange(index, 'surchargesIncluded', e.target.value)}
                                                            >
                                                                <option value="">Select</option>
                                                                <option value="yes">Yes</option>
                                                                <option value="no">No</option>
                                                                <option value="unknown">Unknown</option>
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">6. Which service is covered?</label>
                                                            <select
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                value={entry.coverage}
                                                                onChange={(e) => handleSpendEntryChange(index, 'coverage', e.target.value)}
                                                            >
                                                                <option value="">Select coverage</option>
                                                                {coverageOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Number of shipments (if known)</label>
                                                            <input
                                                                type="number"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                value={entry.shipments}
                                                                onChange={(e) => handleSpendEntryChange(index, 'shipments', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Sub-Q4: Units-based */}
                                {formData.inputMethod === 'units' && (
                                    <div className="space-y-4">
                                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                            <h3 className="font-semibold text-gray-700 mb-2">Sub-Q4: Alternative 2 - Units-based</h3>
                                            <p className="text-sm text-gray-600">
                                                If you are unable to use one of the freight transport options above, we can compute tons per KM if you tell us how many units you shipped.
                                                <br />
                                                <strong>Available modes:</strong> Road, Rail, Air
                                            </p>
                                        </div>
                                        <button
                                            onClick={handleAddUnitsEntry}
                                            className="flex items-center space-x-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all duration-200"
                                        >
                                            <FaPlus className="w-4 h-4" />
                                            <span>Add Units Entry</span>
                                        </button>

                                        {unitsEntries.map((entry, index) => {
                                            const resultKey = `units_${index}`;
                                            const result = state.calculationResults?.[resultKey];

                                            return (
                                                <div key={index} className="p-6 border-2 border-gray-200 rounded-lg space-y-4">
                                                    <div className="flex justify-between items-center">
                                                        <h4 className="font-semibold">Entry {index + 1}</h4>
                                                        <button
                                                            onClick={() => handleRemoveUnitsEntry(index)}
                                                            className="text-red-500 hover:text-red-700 transition-colors duration-200"
                                                        >
                                                            <FaTrash className="w-4 h-4" />
                                                        </button>
                                                    </div>

                                                    {result && (
                                                        <div className="p-3 bg-green-100 rounded-lg border border-green-200">
                                                            <p className="text-sm text-green-800 font-medium">
                                                                Emissions: {result.co2e.toFixed(2)} {result.co2e_unit}
                                                            </p>
                                                            <p className="text-xs text-green-600">
                                                                {entry.mode}: {entry.origin} → {entry.destination}
                                                            </p>
                                                        </div>
                                                    )}

                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Mode</label>
                                                            {renderModeDropdown(entry, index, 'units', handleUnitsEntryChange)}
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">1. Reporting period</label>
                                                            <input
                                                                type="text"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                placeholder="e.g., 2024 (Jan–Dec)"
                                                                value={entry.reportingPeriod}
                                                                onChange={(e) => handleUnitsEntryChange(index, 'reportingPeriod', e.target.value)}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">3. Lane: From (city/port, country)</label>
                                                            <input
                                                                type="text"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                placeholder="e.g., Los Angeles, US"
                                                                value={entry.origin}
                                                                onChange={(e) => handleUnitsEntryChange(index, 'origin', e.target.value)}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">To (city/port, country)</label>
                                                            <input
                                                                type="text"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                placeholder="e.g., San Francisco, US"
                                                                value={entry.destination}
                                                                onChange={(e) => handleUnitsEntryChange(index, 'destination', e.target.value)}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">4. Unit type & count</label>
                                                            <input
                                                                type="text"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2"
                                                                placeholder="e.g., Trucks, Containers, Railcars, ULDs, Flights"
                                                                value={entry.unitType}
                                                                onChange={(e) => handleUnitsEntryChange(index, 'unitType', e.target.value)}
                                                            />
                                                            <input
                                                                type="number"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                placeholder="Count"
                                                                value={entry.unitCount}
                                                                onChange={(e) => handleUnitsEntryChange(index, 'unitCount', e.target.value)}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">5. Typical load per unit</label>
                                                            <div className="flex space-x-2">
                                                                <input
                                                                    type="number"
                                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                                                                    placeholder="Enter value"
                                                                    value={entry.loadPerUnit}
                                                                    onChange={(e) => handleUnitsEntryChange(index, 'loadPerUnit', e.target.value)}
                                                                />
                                                                <select
                                                                    className="px-3 py-2 border border-gray-300 rounded-lg"
                                                                    value={entry.loadUnit}
                                                                    onChange={(e) => handleUnitsEntryChange(index, 'loadUnit', e.target.value)}
                                                                >
                                                                    <option value="tonnes">tonnes (t)</option>
                                                                    <option value="kg">kg</option>
                                                                    <option value="percent">% of capacity</option>
                                                                </select>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">6. Lane distance (km)</label>
                                                            <input
                                                                type="number"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                placeholder="Enter km"
                                                                value={entry.distance}
                                                                onChange={(e) => handleUnitsEntryChange(index, 'distance', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {Object.keys(state.calculationResults || {}).length > 0 && (
                                    <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Calculation Results</h3>
                                        <div className="space-y-2">
                                            {Object.entries(state.calculationResults).map(([key, result]) => (
                                                <div key={key} className="flex justify-between items-center">
                                                    <div>
                                                        <span className="text-gray-700">
                                                            {result.mode}: {result.origin} → {result.destination}
                                                        </span>
                                                        <span className="text-xs text-gray-500 ml-2">
                                                            ({result.inputMethod === 'activity' ? 'Activity-based' :
                                                                result.inputMethod === 'spend' ? 'Spend-based' : 'Units-based'})
                                                        </span>
                                                    </div>
                                                    <span className="font-semibold text-green-700">
                                                        {result.co2e.toFixed(2)} {result.co2e_unit}
                                                    </span>
                                                </div>
                                            ))}
                                            <div className="border-t border-gray-300 pt-2 mt-2">
                                                <div className="flex justify-between items-center font-bold text-lg">
                                                    <span>Total Emissions:</span>
                                                    <span className="text-blue-700">
                                                        {Object.values(state.calculationResults).reduce((sum, result) => sum + (result.co2e || 0), 0).toFixed(2)} kg CO2e
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            onClick={handleCalculate}
                            disabled={state.isCalculating || formData.shipsFreight !== 'yes' || !formData.inputMethod}
                            className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {state.isCalculating ? (
                                <FaSpinner className="w-4 h-4 animate-spin" />
                            ) : (
                                <FaCalculator className="w-4 h-4" />
                            )}
                            <span>{state.isCalculating ? 'Calculating...' : 'Calculate'}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


export default Api5Form;